import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/lib/auth";
import { CartDrawer } from "@/components/layout/CartDrawer";
import { useAppStore } from "@/lib/store";
import { useActiveBanner } from "@/hooks/useBanner";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Dance library", to: "/dance-library", isRoute: true },
  { label: "Backstage talks", to: "/backstage-talk", isRoute: true },
  { label: "Our story", to: "/about-us", isRoute: true },
  { label: "Contact Us", to: "/contact-us", isRoute: true },
];

const logoDark = "/logo-dark.png";

const navLinkClassName =
  "relative mx-3 overflow-hidden py-[clamp(0.375rem,0.339286rem+0.178571vw,0.5rem)] text-[0.88rem] font-semibold leading-6 text-[#545151] transition-colors after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-pink-darker after:transition-[width] after:duration-300 after:content-[''] hover:text-pink-darker hover:after:w-full";
const dashboardButtonClassName =
  "relative inline-flex items-center justify-center whitespace-nowrap rounded-[0.75rem] border-[1.5px] border-pink-dark bg-transparent px-6 py-3 text-center align-middle leading-none text-pink-dark no-underline transition-transform duration-200 ease-[cubic-bezier(.39,.575,.565,1)] active:scale-95 max-[767px]:rounded-[0.5rem] max-[767px]:px-4 max-[767px]:py-[0.7rem]";
const dashboardButtonTextClassName = "relative z-[2] text-[0.88rem] leading-none font-semibold";

function HeartIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 19 19" fill="none" className={className}>
      <path d="M17.2926 7.67188C17.2926 12.5938 9.99487 16.5777 9.68409 16.7422C9.60217 16.7863 9.51061 16.8093 9.4176 16.8093C9.32459 16.8093 9.23303 16.7863 9.15112 16.7422C8.84034 16.5777 1.5426 12.5938 1.5426 7.67188C1.54391 6.5161 2.00361 5.40803 2.82087 4.59077C3.63813 3.77351 4.7462 3.3138 5.90198 3.3125C7.35393 3.3125 8.62518 3.93687 9.4176 4.99227C10.21 3.93687 11.4813 3.3125 12.9332 3.3125C14.089 3.3138 15.1971 3.77351 16.0143 4.59077C16.8316 5.40803 17.2913 6.5161 17.2926 7.67188Z" fill="currentColor" />
    </svg>
  );
}

function CartIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 19 19" fill="none" className={className}>
      <g clipPath="url(#cart-clip)">
        <path d="M17.2722 5.71297L15.4694 12.2014C15.3703 12.5558 15.1583 12.8683 14.8655 13.0914C14.5728 13.3144 14.2153 13.436 13.8473 13.4375H6.8976C6.52848 13.4373 6.16955 13.3164 5.87554 13.0933C5.58153 12.8701 5.36858 12.5569 5.26917 12.2014L2.8026 3.3125H1.5426C1.39342 3.3125 1.25034 3.25324 1.14485 3.14775C1.03937 3.04226 0.980103 2.89918 0.980103 2.75C0.980103 2.60082 1.03937 2.45774 1.14485 2.35225C1.25034 2.24676 1.39342 2.1875 1.5426 2.1875H3.2301C3.35308 2.18748 3.47268 2.22776 3.57058 2.30217C3.66849 2.37658 3.73932 2.48104 3.77221 2.59953L4.43877 5H16.7301C16.8168 4.99998 16.9024 5.02002 16.9801 5.05853C17.0578 5.09705 17.1255 5.15301 17.178 5.22204C17.2305 5.29107 17.2663 5.3713 17.2826 5.45646C17.299 5.54162 17.2954 5.62941 17.2722 5.71297ZM6.6051 14.5625C6.3826 14.5625 6.16509 14.6285 5.98009 14.7521C5.79508 14.8757 5.65089 15.0514 5.56574 15.257C5.48059 15.4625 5.45831 15.6887 5.50172 15.907C5.54513 16.1252 5.65227 16.3257 5.80961 16.483C5.96694 16.6403 6.1674 16.7475 6.38563 16.7909C6.60385 16.8343 6.83005 16.812 7.03562 16.7269C7.24119 16.6417 7.41689 16.4975 7.54051 16.3125C7.66412 16.1275 7.7301 15.91 7.7301 15.6875C7.7301 15.3891 7.61158 15.103 7.4006 14.892C7.18962 14.681 6.90347 14.5625 6.6051 14.5625ZM13.9176 14.5625C13.6951 14.5625 13.4776 14.6285 13.2926 14.7521C13.1076 14.8757 12.9634 15.0514 12.8782 15.257C12.7931 15.4625 12.7708 15.6887 12.8142 15.907C12.8576 16.1252 12.9648 16.3257 13.1221 16.483C13.2794 16.6403 13.4799 16.7475 13.6981 16.7909C13.9164 16.8343 14.1426 16.812 14.3481 16.7269C14.5537 16.6417 14.7294 16.4975 14.853 16.3125C14.9766 16.1275 15.0426 15.91 15.0426 15.6875C15.0426 15.3891 14.9241 15.103 14.7131 14.892C14.5021 14.681 14.216 14.5625 13.9176 14.5625Z" fill="currentColor" />
      </g>
      <defs>
        <clipPath id="cart-clip">
          <rect width="18" height="18" fill="currentColor" transform="translate(0.417603 0.5)" />
        </clipPath>
      </defs>
    </svg>
  );
}

function HamburgerIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M224,128a8,8,0,0,1-8,8H40a8,8,0,0,1,0-16H216A8,8,0,0,1,224,128ZM40,72H216a8,8,0,0,0,0-16H40a8,8,0,0,0,0,16ZM216,184H40a8,8,0,0,0,0,16H216a8,8,0,0,0,0-16Z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" />
    </svg>
  );
}

function PromoBanner() {
  const { data: banner } = useActiveBanner();
  const [dismissedBannerId, setDismissedBannerId] = useState<string | null>(() => {
    try { return sessionStorage.getItem("promo-banner-dismissed-id"); } catch { return null; }
  });

  if (!banner || dismissedBannerId === banner.id) return null;

  const handleDismiss = () => {
    setDismissedBannerId(banner.id);
    try { sessionStorage.setItem("promo-banner-dismissed-id", banner.id); } catch {}
  };

  return (
    <div className="relative z-[3] w-full bg-gradient-to-r from-pink-darker via-pink-dark to-pink-darker px-[var(--site-margin)] py-2.5 text-white shadow-[0_10px_30px_rgba(94,34,74,0.18)] sm:py-3 lg:py-3.5">
      <div className="mx-auto flex max-w-[90rem] flex-col items-center text-center text-[0.85rem] font-semibold leading-snug sm:block sm:pr-8 sm:text-[0.95rem] lg:text-[1rem]">
        <span>
          <span aria-hidden="true" className="mr-1.5 inline-block align-[-0.08em] text-base sm:text-lg">
            🎉
          </span>
          {banner.text}
        </span>
        {banner.link_text && banner.link_url && (
          <Link
            to={banner.link_url}
            className="mx-auto mt-2.5 flex w-fit shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-white/18 px-3 py-1 align-middle text-[0.76rem] font-bold text-white no-underline ring-1 ring-white/20 backdrop-blur-sm transition-colors hover:bg-white/28 sm:mx-0 sm:ml-2 sm:mt-0 sm:inline-flex sm:px-3.5 sm:text-[0.84rem]"
          >
            {banner.link_text}
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
          </Link>
        )}
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute bottom-2 right-[var(--site-margin)] flex h-7 w-7 items-center justify-center rounded-full text-white/75 transition-colors hover:bg-white/10 hover:text-white sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2"
        aria-label="Dismiss banner"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
      </button>
    </div>
  );
}

export function Navbar() {
  const mobileMenuTransitionMs = 220;
  const location = useLocation();
  const { user } = useAuth();
  const { cartItems, setCartOpen } = useAppStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    if (mobileOpen) {
      setMobileMenuVisible(true);
      return;
    }

    if (!mobileMenuVisible) return;

    const timeoutId = window.setTimeout(() => {
      setMobileMenuVisible(false);
    }, mobileMenuTransitionMs);

    return () => window.clearTimeout(timeoutId);
  }, [mobileMenuVisible, mobileOpen, mobileMenuTransitionMs]);

  const isLoggedIn = !!user;
  const dashboardLink = isLoggedIn ? "/app/dashboard/home" : "/app/auth/login";
  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <PromoBanner />
      <header className="relative z-[2] flex min-h-[4.5rem] w-full items-center bg-transparent px-[var(--site-margin)]">
        <div className="container-large grid h-full w-full grid-cols-[auto_1fr] items-center justify-between gap-x-4 gap-y-4 lg:grid-cols-[0.375fr_minmax(0,1fr)_0.375fr]">
          <Link to="/" className="flex shrink-0 items-center justify-self-start">
            <img src={logoDark} alt="BollyRhythm" className="block h-auto w-[9rem] max-[767px]:w-[7rem] max-[479px]:w-[7rem]" />
          </Link>

          <nav className="hidden min-w-0 items-center justify-center justify-self-center lg:flex">
            {navLinks.map((link, i) => {
              const isActive =
                location.pathname === link.to ||
                (link.to === "/backstage-talk" && location.pathname.startsWith("/blog-post/"));
              return (
                <span key={link.label} className="flex items-center">
                  {i > 0 && <span className="h-3 w-[0.13rem] rounded-full bg-[rgb(84_81_81_/_50%)]" />}
                  <Link to={link.to} className={cn(navLinkClassName, isActive && "text-pink-darker")}>
                    {link.label}
                  </Link>
                </span>
              );
            })}
          </nav>

          <div className="hidden items-center justify-self-end lg:flex">
            <div className="ml-[clamp(0.875rem,0.839286rem+0.178571vw,1rem)] flex items-center gap-[10px]">
              <div className="flex items-center justify-between gap-[0.38rem]">
                <Link to="/saved" className="inline-flex size-10 shrink-0 items-center justify-center rounded-[0.75rem] bg-[rgba(94,34,74,0.2)] text-[#5e224a] transition-opacity hover:opacity-90" aria-label="Saved">
                  <HeartIcon className="w-[1.4rem] shrink-0" />
                </Link>
                <button type="button" onClick={() => setCartOpen(true)} className="relative inline-flex size-10 shrink-0 items-center justify-center rounded-[0.75rem] bg-pink-dark text-[#fafafa] transition-opacity hover:opacity-90" aria-label="Cart">
                  <CartIcon className="w-[1.4rem] shrink-0" />
                  {cartItems.length > 0 && (
                    <span className="absolute -top-[0.55rem] -right-[0.45rem] inline-block h-[18px] min-w-[18px] rounded-full bg-white px-[6px] text-center text-[11px] leading-[18px] font-bold text-pink-darker">{cartItems.length}</span>
                  )}
                </button>
              </div>
              <Link to={isLoggedIn ? dashboardLink : "/app/auth/login"} data-testid="navbar-dashboard-link" className={dashboardButtonClassName}>
                <span className={dashboardButtonTextClassName}>{isLoggedIn ? "My Dashboard" : "Login"}</span>
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-[0.28rem] justify-self-end lg:hidden">
            <Link to="/saved" className="inline-flex h-[2.05rem] w-[2.05rem] shrink-0 items-center justify-center rounded-[0.58rem] bg-[rgba(94,34,74,0.2)] text-[#5e224a] transition-opacity hover:opacity-90" aria-label="Saved">
              <HeartIcon className="w-[0.98rem] shrink-0" />
            </Link>
            <button type="button" onClick={() => setCartOpen(true)} className="relative inline-flex h-[2.05rem] w-[2.05rem] shrink-0 items-center justify-center rounded-[0.58rem] bg-pink-dark text-[#fafafa] transition-opacity hover:opacity-90" aria-label="Cart">
              <CartIcon className="w-[0.98rem] shrink-0" />
              {cartItems.length > 0 && (
                <span className="absolute -top-[0.38rem] -right-[0.32rem] inline-block h-[15px] min-w-[15px] rounded-full bg-white px-[4px] text-center text-[9px] leading-[15px] font-bold text-pink-darker">{cartItems.length}</span>
              )}
            </button>
            <button type="button" className="inline-flex h-[2.05rem] w-[2.05rem] items-center justify-center rounded-[0.58rem] text-[#241d22] transition-colors hover:bg-[rgba(94,34,74,0.1)]" aria-label={mobileOpen ? "Close menu" : "Open menu"} onClick={() => setMobileOpen((prev) => !prev)}>
              {mobileOpen ? <XIcon className="h-[0.98rem] w-[0.98rem]" /> : <HamburgerIcon className="h-[0.98rem] w-[0.98rem]" />}
            </button>
          </div>
        </div>
      </header>

      {createPortal(
        mobileMenuVisible ? (
          <>
            <div
              className={cn(
                "fixed inset-0 bg-black/30 transition-opacity ease-out lg:hidden",
                mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
              )}
              style={{ zIndex: 100000, transitionDuration: `${mobileMenuTransitionMs}ms` }}
              onClick={closeMobile}
            />
            <div
              className={cn(
                "fixed inset-y-0 right-0 flex w-[min(20rem,85vw)] flex-col bg-white shadow-xl transition-opacity ease-out lg:hidden",
                mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
              )}
              style={{ zIndex: 100001, transitionDuration: `${mobileMenuTransitionMs}ms` }}
              aria-hidden={!mobileOpen}
            >
              <div className="flex min-h-[4.5rem] items-center justify-between border-b border-[#e2e4e9] px-5">
                <Link to="/" onClick={closeMobile}>
                  <img src={logoDark} alt="BollyRhythm" className="h-auto w-[7rem]" />
                </Link>
                <button type="button" className="inline-flex size-9 items-center justify-center rounded-[0.65rem] text-[#241d22]" aria-label="Close menu" onClick={closeMobile}>
                  <XIcon className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto px-5 pt-6">
                <div className="flex flex-col gap-1">
                  {navLinks.map((link) => {
                    const isActive =
                      location.pathname === link.to ||
                      (link.to === "/backstage-talk" && location.pathname.startsWith("/blog-post/"));
                    return (
                      <Link key={link.label} to={link.to} className={cn("rounded-[0.5rem] px-3 py-3 text-[0.95rem] font-semibold text-[#545151] transition-colors hover:bg-[#f6f8fa] hover:text-pink-darker", isActive && "bg-[#f6f8fa] text-pink-darker")} onClick={closeMobile}>
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              </nav>
              <div className="border-t border-[#e2e4e9] px-5 py-5">
                <Link to={isLoggedIn ? dashboardLink : "/app/auth/login"} className="flex w-full items-center justify-center rounded-[0.75rem] border-[1.5px] border-pink-dark bg-pink-dark px-6 py-3 text-center text-[0.88rem] font-semibold leading-none text-white no-underline transition-transform duration-200 active:scale-95" onClick={closeMobile}>
                  {isLoggedIn ? "My Dashboard" : "Login"}
                </Link>
              </div>
            </div>
          </>
        ) : null,
        document.body
      )}

      <CartDrawer />
    </>
  );
}
